import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Box,
  Select, // Importar Select
  MenuItem, // Importar MenuItem para as opções do Select
  FormControl, // Importar FormControl para o Select
  InputLabel,
  Tooltip, // Importar InputLabel para o Select
  Collapse,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortIcon from "@mui/icons-material/Sort"; // Importar um ícone genérico de ordenação
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Fragment, useState } from "react";
import shortenString from "../../../../utils/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import VolumeItemsDetails from "./VolumeItemsDetails";

const isVolumeAttended = (status) =>
  status === 3 ||
  status === "3" ||
  (typeof status === "string" &&
    status.trim().toUpperCase() === "TOTALMENTE ATENDIDO");

/**
 * Componente refatorado para exibir uma tabela de itens de carregamento.
 * @param {Object} props As propriedades do componente.
 * @param {string} props.title O título da tabela.
 * @param {Array<Object>} props.items A lista de itens a serem exibidas.
 * @param {Array<Object>} [props.comparisonItems=[]] Itens para comparação (usado para destacar linhas).
 * @param {boolean} [props.showStatusIcon=false] Se deve mostrar o ícone de status (check/off).
 * @param {boolean} [props.showRemoveButton=false] Se deve mostrar o botão de remover.
 * @param {Function} [props.onRemoveItem] Função de callback para remover um item.
 * @param {string} [props.type] Indica qual lista é renderizada "expected" ou "received".
 * @param {boolean} [props.showDetailsButton=false] Se deve mostrar o botão de detalhes do volume.
 */
const ValidateShipmentTable = ({
  title,
  items,
  comparisonItems = [],
  showStatusIcon = false,
  showRemoveButton = false,
  onRemoveItem,
  type,
  filterDescription,
  setFilterDescription,
  showFilterInput = true,
  showSerial = false,
  showDetailsButton = false,
}) => {
  const [filterStatus, setFilterStatus] = useState("Todos"); // Novo estado para o filtro de status
  const [orderBy, setOrderBy] = useState("");
  const [orderDirection, setOrderDirection] = useState("asc"); // 'asc' ou 'desc'
  // * Guarda o id do volume expandido. Os itens só são buscados ao expandir.
  const [expandedVolumeId, setExpandedVolumeId] = useState(null);

  /**
   * Determina a cor de fundo da linha da tabela com base no status do item e na comparação.
   * @param {Object} item O item atual da linha.
   * @param {boolean} isMatched Indica se o item foi encontrado na lista de comparação.
   * @param {string} type Indica qual lista é renderizada "expected" ou "received".
   * @returns {string} A cor de fundo em formato hexadecimal ou "inherit".
   */
  const getRowBackgroundColor = (item, isMatched, type) => {
    const isAttended = isVolumeAttended(item.status);

    if (type === "expected") {
      if (isMatched && isAttended) return "#ffc5ce"; // vermelho (duplicado)
      if (!isMatched && isAttended) return "#b6f6bb"; // verde (atendido mas não duplicado)
      if (isMatched && !isAttended) return "#b6f6bb"; // verde (verificado mas ainda faltando)
      if (!isMatched && !isAttended) return "#ffc5ce"; // vermelho (faltando)
    }

    if (type === "received") {
      // Aqui não existe item.status, então vamos buscar o item original na lista de comparação
      const matchingExpectedItem = comparisonItems.find(
        (i) => String(i.id) === String(item.id),
      );
      const isAlreadyAttended = isVolumeAttended(matchingExpectedItem?.status);

      if (isAlreadyAttended || !isMatched) return "#ffc5ce"; // vermelho (duplicado ou não encontrado no esperado)
      return "#b6f6bb"; // verde (válido)
    }

    return "inherit";
  };

  /**
   * Retorna o status textual e o ícone para a coluna de status.
   * @param {Object} item O item atual.
   * @param {boolean} isMatched Indica se o item foi encontrado na lista de comparação.
   * @returns {Object} Um objeto com a mensagem de status, o ícone e a cor.
   */
  const getStatusDisplay = (item, isMatched) => {
    if (type === "expected") {
      if (isVolumeAttended(item.status)) {
        return {
          message: isMatched ? "Atendido (Duplicado)" : "Atendido",
          icon: <CheckCircleIcon color="success" />,
          color: isMatched ? "error" : "success",
          filterValue: "Verificado", // Valor para o filtro e ordenação
        };
      } else {
        return {
          message: isMatched ? "Verificado" : "Faltando",
          icon: isMatched ? (
            <CheckCircleIcon color="success" />
          ) : (
            <HighlightOffIcon color="error" />
          ),
          color: isMatched ? "success" : "error",
          filterValue: isMatched ? "Verificado" : "Faltando", // Valor para o filtro e ordenação
        };
      }
    } else if (type === "received") {
      const matchingExpectedItem = comparisonItems.find(
        (i) => String(i.id) === String(item.id),
      );
      const isAlreadyAttended = isVolumeAttended(matchingExpectedItem?.status);

      if (isAlreadyAttended || !isMatched) {
        return {
          message: isAlreadyAttended ? "Atendido (Duplicado)" : "Não Esperado",
          icon: <HighlightOffIcon color="error" />,
          color: "error",
          filterValue: "Faltando", // Consideramos "Não Esperado" como um tipo de "Faltando" para a lógica do filtro e ordenação
        };
      } else {
        return {
          message: "Verificado",
          icon: <CheckCircleIcon color="success" />,
          color: "success",
          filterValue: "Verificado", // Valor para o filtro e ordenação
        };
      }
    }
    return { message: "", icon: null, filterValue: "" };
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedAndFilteredItems =
    Array.isArray(items) && items?.length > 0
      ? [...items]
      : []
          .filter((item) => {
            const descriptionMatch = item.description
              ?.toLowerCase()
              .includes(filterDescription.toLowerCase());
            const statusInfo = getStatusDisplay(
              item,
              comparisonItems.find((i) => String(i.id) === String(item.id)),
            );

            const statusMatch =
              filterStatus === "Todos" ||
              statusInfo.filterValue === filterStatus;

            return descriptionMatch && statusMatch;
          })
          .sort((a, b) => {
            if (orderBy === "description") {
              return orderDirection === "asc"
                ? a.description.localeCompare(b.description)
                : b.description.localeCompare(a.description);
            }
            if (orderBy === "status") {
              // Alterado para ordenar por 'status'
              const statusA = getStatusDisplay(
                a,
                comparisonItems.find((i) => String(i.id) === String(a.id)),
              ).filterValue;
              const statusB = getStatusDisplay(
                b,
                comparisonItems.find((i) => String(i.id) === String(b.id)),
              ).filterValue;

              // Lógica de ordenação para 'Verificado' e 'Faltando'
              // Você pode ajustar a ordem de prioridade aqui. Ex: 'Faltando' primeiro.
              if (statusA === "Faltando" && statusB === "Verificado") {
                return orderDirection === "asc" ? 1 : -1;
              }
              if (statusA === "Verificado" && statusB === "Faltando") {
                return orderDirection === "asc" ? -1 : 1;
              }
              return 0; // Se forem o mesmo status, a ordem não importa
            }
            return 0;
          });

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          backgroundColor: "rgba(228, 228, 228, 1)",
          px: 3,
          py: 1,
          fontWeight: "bold",
          color: "rgb(83, 82, 82)",
        }}
        variant="h6"
        gutterBottom
      >
        {title}
      </Typography>
      <Box sx={{ px: 2, pb: 2, flexGrow: 1 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
          {showFilterInput ? (
            <TextField
              label="Filtrar por descrição"
              variant="outlined"
              size="small"
              value={filterDescription}
              onChange={(e) => setFilterDescription(e.target.value)}
              fullWidth
            />
          ) : (
            <TextField
              label="Filtrar por descrição"
              variant="outlined"
              size="small"
              value={filterDescription}
              onChange={(e) => setFilterDescription(e.target.value)}
              fullWidth
              className="invisible"
            />
          )}
          {!showRemoveButton && ( // Apenas mostra o filtro de status se não for a tabela "Patrimônios Verificados"
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="filter-status-label">
                Filtrar por Status
              </InputLabel>
              <Select
                labelId="filter-status-label"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filtrar por Status"
              >
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="Verificado">Verificado</MenuItem>
                <MenuItem value="Faltando">Faltando</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
              <TableRow>
                <TableCell
                  onClick={() => handleSort("description")}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: "#d1e2e7ff",
                  }}
                >
                  Descrição
                  <SortIcon fontSize="inherit" sx={{ ml: 1, opacity: 0.6 }} />
                  {orderBy === "description" &&
                    (orderDirection === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    ))}
                </TableCell>
                {showSerial && <TableCell>Serial</TableCell>}
                {!showRemoveButton ? (
                  <TableCell
                    onClick={() => handleSort("status")}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: "#d1e2e7ff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      Status
                      <SortIcon
                        fontSize="inherit"
                        sx={{ ml: 1, opacity: 0.6 }}
                      />
                      {orderBy === "status" &&
                        (orderDirection === "asc" ? (
                          <ArrowUpwardIcon fontSize="small" />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" />
                        ))}
                    </Box>
                  </TableCell>
                ) : (
                  <TableCell
                    onClick={() => handleSort("status")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    Status
                  </TableCell>
                )}
                {showDetailsButton && (
                  <TableCell
                    sx={{
                      backgroundColor: "#d1e2e7ff",
                      width: 100,
                    }}
                    align="right"
                  >
                    Qtd. Total
                  </TableCell>
                )}
                {showRemoveButton && <TableCell></TableCell>}
                {showDetailsButton && (
                  <TableCell
                    sx={{
                      backgroundColor: "#d1e2e7ff",
                      width: 110,
                    }}
                  >
                    Detalhes
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndFilteredItems.length > 0 ? (
                sortedAndFilteredItems.map((item) => {
                  const isMatched = comparisonItems.find(
                    (i) => String(i.id) === String(item.id),
                  );
                  const backgroundColor = getRowBackgroundColor(
                    item,
                    isMatched,
                    type,
                  );
                  const statusDisplay = getStatusDisplay(item, isMatched);
                  const shortedDescription = shortenString(
                    item.description,
                    32,
                  );
                  // const shortedSerial = shortenString(item?.serial, 15);
                  const isExpanded = expandedVolumeId === item.id;
                  const columnCount =
                    (showStatusIcon ? 1 : 0) +
                    (showRemoveButton ? 1 : 0) +
                    (showSerial ? 1 : 0) +
                    (showDetailsButton ? 2 : 0) +
                    1;

                  return (
                    <Fragment key={item.id}>
                      <TableRow sx={{ height: 48, backgroundColor }}>
                        <TableCell>
                          <Tooltip title={item.description}>
                            <span>{shortedDescription}</span>
                          </Tooltip>
                        </TableCell>
                        {showSerial && (
                          <TableCell>
                            <Tooltip title={item?.serial || ""}>
                              <span>{item?.serial || "-"}</span>
                            </Tooltip>
                          </TableCell>
                        )}
                        {showStatusIcon && (
                          <TableCell>
                            <div className="d-flex align-items-center gap-1">
                              {statusDisplay.icon}
                              <Typography
                                variant="body2"
                                color={statusDisplay.color}
                              >
                                {statusDisplay.message}
                              </Typography>
                            </div>
                          </TableCell>
                        )}
                        {showDetailsButton && (
                          <TableCell align="right">
                            {item.total_quantity ?? "-"}
                          </TableCell>
                        )}
                        {showRemoveButton && onRemoveItem && (
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onRemoveItem(item.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </IconButton>
                          </TableCell>
                        )}
                        {showDetailsButton && (
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setExpandedVolumeId(
                                  isExpanded ? null : item.id,
                                )
                              }
                              startIcon={
                                isExpanded ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )
                              }
                              sx={{ textTransform: "none" }}
                            >
                              Detalhes
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                      {showDetailsButton && (
                        <TableRow>
                          <TableCell
                            colSpan={columnCount}
                            sx={{ paddingBottom: 0, paddingTop: 0 }}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <VolumeItemsDetails
                                movementId={item.movement_id}
                                volumeId={item.id}
                                open={isExpanded}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={
                      (showStatusIcon ? 1 : 0) +
                        (showRemoveButton ? 1 : 0) +
                        (showSerial ? 1 : 0) +
                        (showDetailsButton ? 2 : 0) +
                        1
                    }
                    align="center"
                  >
                    {title.includes("Esperado")
                      ? "Nenhum carregamento selecionado ou itens não encontrados nos filtros."
                      : "Esse movimento não possui itens ou itens não encontrados nos filtros."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default ValidateShipmentTable;
